import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IContextualMenuItem, CommandBar, CommandBarButton, Icon, ICommandBarItemProps, DayOfWeek, memoizeFunction } from 'office-ui-fabric-react';
import { ApplicationState } from '../../../../store';
import * as ResourcesListStore from '../../../../store/ResourcesListStore';
import * as CostRateTableStore from '../../../../store/CostRateTableStore';
import { UserState } from '../../../../store/User';
import { getFirstDayOfWeekSetting } from '../../../../store/selectors/settingsSelectors';
import { CommonOperations, contains } from '../../../../store/permissions';
import { IListProps } from '../../../../components/common/extensibleEntity/EntityDetailsList';
import { TimelineGroupingProps, IRow, TimelineChange } from '../../../../components/common/timeline/TimelineList';
import { IExtensibleEntity, Dictionary, Quantization, ITimeframe, EntityType, IWithResourcePlan, IScalableTimeframe, entityTypeLabelMap, EstimatedChargeCalculationMode } from '../../../../entities/common';
import { OwnProps as ListMenuProps } from '../../../../components/common/extensibleEntity/ListMenu';
import { ResourceUsageCell, ISelectableCellData, TimeTypeSelector } from '../ResourceUsageCell';
import { ITimelineProps } from '../../../../components/common/extensibleEntity/EntityTimelineList';
import { ITimelineSegment } from '../../../../components/common/timeline/TimelineSegment';
import { buildSetRateCommand, SetTimeAction, buildAllocateCommands, buildAutoAllocateCommand, buildSetBillingCodeCommand, buildSetCostRateTableCommand,
    buildSetStateInEntityCommand
} from '../ResourceUsageCell';
import { notUndefined, distinct, formatFieldValue, HUNDRED_PCT, DebouncedAction } from '../../../utils/common';
import { CalculatedField, Field, FieldGroup, FieldType, FormatType } from '../../../../entities/Metadata';
import RemoveDialog from '../../../common/RemoveDialog';
import { IOrderBy } from '../../../../store/views';
import { ShiftAllocationPanel } from '../ShiftAllocationPanel';
import { max, minMax } from '../../../common/timeline/utils';
import { nameof } from '../../../../store/services/metadataService';
import { MyWorkAttrs } from '../../../../store/MyWorkStore';
import { LocationState } from 'history';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { ActiveFilter, MyWorkParentInfo } from '../../../../store/mywork/filters';
import { AllocatePanel } from '../AllocatePanel';
import { TextFormatter } from '../../../common/formatters/TextFormatter';
import { ConfirmationDialog } from '../../../common/ConfirmationDialog';
import * as TimephasedValueService from '../../../../services/TimephasedValueService';
import Link from '../../../common/Link';
import BaseUsageGrid, { MenuRenderProps, SegmentContentProps, TooltipRenderProps } from './BaseUsageGrid';
import { allTimeTypes, CellSummaryContext, CellSummaryData, TimeType, TimeTypesConfig } from '../ResourceUsageCell/common';
import { StateInEntityView } from '../../../views/list/columns/resource/StateInEntity';

export type ShiftPlanContext = { entityId: string, resourceId: string, days: number };

export type CellTooltipRenderProps = {
    resource: ResourcesListStore.Resource;
    entityId: string;
    entityType: EntityType;
    startDate: Date;
    finishDate: Date;
    onMenuRender?: () => JSX.Element;
};

export type TotalTooltipRenderProps = {
    resources: ResourcesListStore.Resource[];
    entityId?: string;
    entityType: EntityType;
    startDate: Date;
    finishDate: Date;
};

export enum ViewType {
    Hours,
    Percent,
    FTE,
    Chart
}

const duration_days = 210;
export const defaultTimeframe: ITimeframe = {
    start: new Date().getMonthStartDate(),
    end: new Date().getMonthStartDate().addDays(duration_days).getMonthEndDate()
}

export interface IMaybeTotal { isTotal?: true };
export const totalRow: IExtensibleEntity & IMaybeTotal = {
    id: 'totalRow',
    attributes: {},
    isTotal: true
};
export const SCALE_MULTIPLIER_PER_TIMETYPE_COLUMN = 1.5;

export type OwnProps = {
    entityType: EntityType;
    byResource?: boolean;
    entitiesMap: Dictionary<IExtensibleEntity & IWithResourcePlan>;
    scrollable?: boolean;
    entitiesIds: string[];
    selectedEntityIds?: string[];
    viewType?: ViewType;
    isLoading: boolean;
    menuProps: ListMenuProps;
    listProps: IListProps & Partial<ITimelineProps>;
    timelineGrouping?: TimelineGroupingProps;
    isEditable?: ((entity: IExtensibleEntity) => boolean);
    extractRowIds: (entity: IExtensibleEntity) => { entityIds: string[], resourceIds: string[]; isTotalRow?: boolean; };
    extractCellIds: (cell: ISelectableCellData) => { entityId: string, resourceId: string; };
    renderTotalSegmentContent?: (row: IRow, segment: ITimelineSegment, timeType: TimeType[]) => JSX.Element;
    renderCellTooltip: (props: CellTooltipRenderProps) => JSX.Element;
    renderTotalTooltip: (props: TotalTooltipRenderProps) => JSX.Element | undefined;
    buildRowMenuItems?: (entity: IExtensibleEntity) => IContextualMenuItem[];
    onTimeframeChanged?: (timeframe: ITimeframe) => void;

    timeType: TimeType[];
    onTimeTypeChange: (timeType: TimeType[]) => void;
    timeTypesConfig: TimeTypesConfig;
    cellSummaryCalculator: (startDate: Date, finishDate: Date, context: CellSummaryContext) => CellSummaryData;
    calculatedFields: CalculatedField[];

    actualTimeDisabled?: boolean;
    commitDisabled?: boolean;
}

type ActionProps = {
    resourceActions: typeof ResourcesListStore.actionCreators;
}
type StoreProps = {
    user: UserState;
    resources: ResourcesListStore.ResourcesState,
    parentToChildrenMap: Dictionary<MyWorkParentInfo[]>;
    firstDayOfWeek: DayOfWeek;
    costRateTables: CostRateTableStore.CostRateTableState;
}
type Props = OwnProps & ActionProps & StoreProps & RouteComponentProps<{}>;

enum CommittingType {
    Commit = 1,
    Uncommit = 2
}

interface State {
    timeframe: ITimeframe;
    isCommitting?: CommittingType;
    isShiftPlanPanelOpen?: boolean;
    isAllocatePanelOpen?: boolean;
    isSetActualPanelOpen?: boolean;
    isChargeRateInfoDialogOpen?: boolean;
    pendingCostRateTableChange?: {
        costRateTableId: string;
        costRateTableName: string;
        pairs: { entityId: string; resourceId: string; }[];
    };
    unableToChangeCostRateTable?: {
        costRateTableName: string;
        resourceNames: string[];
    };
}

const calculateSum = (
    resources: ResourcesListStore.Resource[],
    entityIds: string[],
    attributeSelector: (usage: ResourcesListStore.EntityResourceUsageAttrs) => number,
    resourceAction?: (resource: ResourcesListStore.Resource, usageSum: number) => number) => {
    resourceAction ??= (_, s) => s;
    return resources.reduce((sum, resource) => {
        const sumByResource = entityIds.reduce((acc, entityId) => {
            const entityUsages = resource.usage?.byEntity[entityId];
            return acc + (entityUsages ? resourceAction!(resource, attributeSelector(entityUsages.attributes)) : 0);
        }, 0);
        return sum + sumByResource;
    }, 0);
}

// copy is on server side in Consts.cs
export const bookingTypeField: CalculatedField = {
    id: 'dbc93574-04d8-40c6-b349-4c012e218435',
    type: FieldType.Text,
    name: "dbc93574-04d8-40c6-b349-4c012e218435",
    label: 'Booking Type',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) => {
        if (resources.length !== 1 || entityIds.length !== 1) {
            return undefined;
        }

        for (const entityId of entityIds) {
            const result = resources[0].usage?.byEntity[entityId]?.attributes.bookingType;
            if (result !== undefined) {
                return result;
            }
        }
        return undefined;
    },
}

export const stateInEntityFieldId = '5a1c6f83-9b74-4de2-8a35-c07e1f2b4d96';

export const calculateStateInEntity = (resources: ResourcesListStore.Resource[], entityIds: string[]) => {
    if (resources.length !== 1 || entityIds.length !== 1) {
        return undefined;
    }

    return resources[0].usage?.byEntity[entityIds[0]]?.attributes.stateInEntity;
}

export const buildStateInEntityField = memoizeFunction((entityType: EntityType): CalculatedField => ({
    id: stateInEntityFieldId,
    type: FieldType.Text,
    name: stateInEntityFieldId,
    label: `State in ${entityTypeLabelMap[entityType].singular}`,
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: calculateStateInEntity
}));

export const chargeRateField: CalculatedField = {
    id: 'c8ec6cd0-0d5e-4697-997c-717bd1f91efd',
    type: FieldType.Decimal,
    name: "c8ec6cd0-0d5e-4697-997c-717bd1f91efd",
    label: 'Charge Rate',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) => {
        if (resources.length !== 1 || entityIds.length !== 1) {
            return undefined;
        }

        const entityUsage = resources[0].usage?.byEntity[entityIds[0]];
        if (!entityUsage) {
            return undefined;
        }

        const mode = entityUsage.attributes.estimatedChargeCalculationMode ?? EstimatedChargeCalculationMode.UseChargeRate;
        if (mode === EstimatedChargeCalculationMode.UseChargeRate) {
            return entityUsage.attributes.chargeRate;
        }

        return undefined;
    },
}

export const billingCodeField: CalculatedField = {
    id: '853870ab-0132-4696-9185-69eb6763600a',
    type: FieldType.Text,
    name: "853870ab-0132-4696-9185-69eb6763600a",
    label: 'Billing Code',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) => {
        if (resources.length !== 1 || entityIds.length !== 1) {
            return undefined;
        }

        return resources[0].usage?.byEntity[entityIds[0]]?.attributes.billingCode;
    }
};

export const costRateTableField: CalculatedField = {
    id: 'f7a3e8b2-1c4d-4e9a-8f5b-2d6c9a7e4b1f',
    type: FieldType.Text,
    name: "f7a3e8b2-1c4d-4e9a-8f5b-2d6c9a7e4b1f",
    label: 'Cost Rate Table',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) => {
        if (resources.length !== 1 || entityIds.length !== 1) {
            return undefined;
        }

        return resources[0].usage?.byEntity[entityIds[0]]?.attributes.costRateTableId;
    }
};

const estimatedCostField: CalculatedField = {
    id: '07c2b4a1-96f1-43f7-a6da-6bed0177e3a5',
    type: FieldType.Decimal,
    name: "07c2b4a1-96f1-43f7-a6da-6bed0177e3a5",
    label: 'Estimated Cost',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) =>
        calculateSum(
            resources,
            entityIds,
            attrs => attrs.estimatedCost)
}

const actualCostField: CalculatedField = {
    id: "e8147ff7-ac12-410b-8b0c-30729d270ba1",
    type: FieldType.Decimal,
    name: "e8147ff7-ac12-410b-8b0c-30729d270ba1",
    label: 'Actual Cost',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) =>
        calculateSum(
            resources,
            entityIds,
            attrs => attrs.actualCost)
}

const estimatedChargeField: CalculatedField = {
    id: '9e96cde8-8e21-40bb-b73d-5d8bf434ea61',
    type: FieldType.Decimal,
    name: "9e96cde8-8e21-40bb-b73d-5d8bf434ea61",
    label: 'Estimated Charge',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) =>
        calculateSum(
            resources,
            entityIds,
            attrs => attrs.estimatedCharge)
}

export const totalPlannedField: CalculatedField = {
    id: '97225117-647f-4d35-8186-6bd4eb761f32',
    type: FieldType.Decimal,
    name: '97225117-647f-4d35-8186-6bd4eb761f32',
    label: 'Total Planned',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) =>
        calculateSum(
            resources,
            entityIds,
            attrs => attrs.totalPlanned)
}

const totalActualField: CalculatedField = {
    id: 'ef26842f-995f-4095-991e-a17c70d204d2',
    type: FieldType.Decimal,
    name: 'ef26842f-995f-4095-991e-a17c70d204d2',
    label: 'Total Actual',
    isNative: true,
    isCustom: false,
    isReadonly: true,
    isSystem: true,
    group: FieldGroup.SystemFields,
    calculate: (resources: ResourcesListStore.Resource[], entityIds: string[]) =>
        calculateSum(
            resources,
            entityIds,
            attrs => attrs.totalActual)
}

export const totalsFieldIds = [
    estimatedCostField.id,
    estimatedChargeField.id,
    totalPlannedField.id,
    totalActualField.id,
    actualCostField.id
];

export const buildResourcePlanCalculatedFields = memoizeFunction((entityType: EntityType, isArchived?: boolean): CalculatedField[] => [
    bookingTypeField,
    ...(isArchived ? [] : [buildStateInEntityField(entityType)]),
    chargeRateField,
    billingCodeField,
    costRateTableField,
    estimatedCostField,
    estimatedChargeField,
    totalPlannedField,
    totalActualField,
    actualCostField
]);

export const calculateResourceUsageGridField = (
    field: CalculatedField,
    resources: ResourcesListStore.Resource[],
    entityIds: string[],
    costRateTables?: CostRateTableStore.CostRateTableState,
) => {
    const value = field.calculate(resources, entityIds);
    if (field === bookingTypeField) {
        return value ? "COMMITTED" : "PROPOSED";
    }
    if (field.id === stateInEntityFieldId) {
        const state = value as ResourcesListStore.ResourceEntityState | undefined;
        return state === undefined ? undefined : ResourcesListStore.ResourceEntityStateConfigMap[state]?.title;
    }
    if (field === costRateTableField) {
        const id = value as string | undefined;
        if (!id) {
            return undefined;
        }

        return costRateTables?.byId?.[id]?.name ?? id;
    }
    return value;
}

type BaseCalculatedFieldFormatterProps = {
    resources: ResourcesListStore.ResourcesState,
    entityIds: string[],
    resourceIds: string[],
    className?: string,
    user: UserState
}
type CalculatedFieldFormatterProps = BaseCalculatedFieldFormatterProps & {
    calculatedField: CalculatedField,
    formatType: FormatType,
}

export const CalculatedFieldFormatter = React.memo((props: CalculatedFieldFormatterProps) => {
    const { user, calculatedField, formatType, resources, resourceIds, className, entityIds } = props;
    const value = React.useMemo(
        () => calculatedField.calculate(resourceIds.map(_ => resources.byId[_]).filter(notUndefined), entityIds),
        [resourceIds, resources, entityIds]);

    return <span className={className}>
        {value !== undefined ? formatFieldValue(value, formatType, user) : null}
    </span>;
})

class ResourceUsageGrid extends React.Component<Props, State>{
    private readonly _debouncedSetPlan: DebouncedAction<ResourcesListStore.ResourceUsageUpdate[]>;
    private readonly _debouncedSetActual: DebouncedAction<ResourcesListStore.ResourceUsageUpdate[]>;

    constructor(props: Props) {
        super(props);

        this._debouncedSetPlan = new DebouncedAction(
            (data: ResourcesListStore.ResourceUsageUpdate[][]) => {
                if (data) {
                    const usage = data.reduce<ResourcesListStore.ResourceUsageUpdate[]>((acc, item) => [...acc, ...item], []);
                    this.props.resourceActions.updateUsage(this.props.entityType, usage, convertToDateOnlyTimeframe(this.state.timeframe), false);
                }
            }
        )
        this._debouncedSetActual = new DebouncedAction(
            (data: ResourcesListStore.ResourceUsageUpdate[][]) => {
                if (data) {
                    const usage = data.reduce<ResourcesListStore.ResourceUsageUpdate[]>((acc, item) => [...acc, ...item], []);
                    this.props.resourceActions.updateUsage(this.props.entityType, usage, convertToDateOnlyTimeframe(this.state.timeframe), true);
                }
            }
        )

        this.state = {timeframe: { ...defaultTimeframe, ...props.listProps.userTimeframe }};
    }

    render() {
        const { isLoading, timelineGrouping, isEditable } = this.props;

        const viewType = this.props.viewType ?? ViewType.Hours;

        const menuProps = this._buildMenuProps();
        const listProps: IListProps & Partial<ITimelineProps> = this._buildListProps();

        return <>
            <BaseUsageGrid 
                scrollable={this.props.scrollable}
                entitiesIds={this.props.entitiesIds}
                selectedEntityIds={this.props.selectedEntityIds}
                isLoading={isLoading}
                menuProps={menuProps}
                listProps={listProps}
                timelineGrouping={timelineGrouping}
                isEditable={isEditable}
                onTimeframeChanged={_ => {
                    this.setState({ timeframe: _ }); 
                    this.props.onTimeframeChanged?.(_);
                }}
                buildRowMenuItems={(item) => {
                    let commands = this.props.buildRowMenuItems?.(item) || [];
                    if (isEditable?.(item)) {
                        const costRateTablesForRow = this._getAvailableCostRateTablesForRow(item);
                        commands = [
                            this._buildAllocateCommand(),
                            this._buildSetActualCommand(),
                            this._buildShiftAllocationCommand(),
                            buildSetStateInEntityCommand(state => this._setStateInEntity(state, [item]), this.props.entityType,
                                this._getStateInEntityForRows([item])),
                            buildSetCostRateTableCommand(id => this._requestSetCostRateTable(id, [item]), costRateTablesForRow),
                            this._buildSetRateCommandIfCanManageBudget([item]),
                            buildSetBillingCodeCommand(code => this._setBillingCode(code, [item])),
                            this._buildCommitUncommitCommand(item),
                            ...commands
                        ].filter(notUndefined);
                    }
                    return commands;
                }}
                renderSegmentContent={(segmentProps: SegmentContentProps) => {
                    const { row, segment, quantization, selectionIndex , cellSelection, componentRef, onClick, onKeyDown, onKeyPress, onEditStart, onEditCompleteBuilder } = segmentProps;
                    const { entityIds, resourceIds, isTotalRow } = this.props.extractRowIds(row.entity);
                    if (isTotalRow) {
                        return this.props.renderTotalSegmentContent?.(row, segment, this.props.timeType);
                    }

                    const onEditComplete = onEditCompleteBuilder((value: number | null, timeType: TimeType) => {
                            if (value !== null) {
                                (timeType === 'actual' ? this._setActual : this._setPlan) (
                                    [(cellSelection.getItems() as ISelectableCellData[]).find(_ => _.key === selectionIndex)!],
                                    value,
                                    viewType === ViewType.Chart ? ViewType.Hours : viewType);
                            }
                        });

                    return <ResourceUsageCell
                        timeType={this.props.timeType}
                        summaryCalculator={this.props.cellSummaryCalculator}
                        timeTypesConfig={this.props.timeTypesConfig}
                        resource={this.props.resources.byId[resourceIds[0]]}
                        entityId={entityIds[0]}
                        entityType={this.props.entityType}
                        selection={cellSelection}
                        selectionIndex={selectionIndex}
                        componentRef={componentRef}
                        onClick={onClick}
                        onKeyDown={onKeyDown}
                        onKeyPress={onKeyPress}
                        onEditStart={onEditStart}
                        onEditComplete={onEditComplete}
                        viewType={viewType}
                        quantization={quantization}
                        prevStartDate={segment.prevStartDate}
                        startDate={segment.startDate}
                        finishDate={segment.finishDate} />;
                }}
                renderTooltip={(tooltipProps: TooltipRenderProps) => {
                    const { entity, segment, quantization } = tooltipProps;
                    const { entityIds, resourceIds, isTotalRow } = this.props.extractRowIds(entity);
                    if (isTotalRow) {
                        return this.props.renderTotalTooltip({
                            resources: resourceIds.map(_ => this.props.resources.byId[_]).filter(notUndefined),
                            entityId: entityIds.length === 1 ? entityIds[0] : undefined,
                            entityType: this.props.entityType,
                            startDate: segment.startDate,
                            finishDate: segment.finishDate
                        });
                    }
                    const cell = { entityId: entity.id, start: segment.startDate, finish: segment.finishDate };
                    return this.props.renderCellTooltip({
                        resource: this.props.resources.byId[resourceIds[0]],
                        entityId: entityIds[0],
                        entityType: this.props.entityType,
                        startDate: segment.startDate,
                        finishDate: segment.finishDate,
                        onMenuRender: isEditable?.(entity)
                            ? () => <CellMenu cell={cell}
                                quantization={quantization}
                                setPlan={(_plan, _viewType, _cells) => this._setPlan(_cells ?? [cell], _plan, _viewType)}
                                setActual={!this.props.actualTimeDisabled
                                    ? (_plan, _viewType, _cells) => this._setActual(_cells ?? [cell], _plan, _viewType)
                                    : undefined}
                                viewAssignments={!this.props.actualTimeDisabled
                                    ? { action: () => this._viewAssignments([cell]), disabled: !this._canResourceView(resourceIds[0]) }
                                    : undefined} />
                            : undefined
                    });
                }}
                renderCellMenu={(cellMenuProps: MenuRenderProps) => {
                    const { cells, quantization } = cellMenuProps;
                    const cellsFromSingleResourceSelected = cells
                        .map(_ => this.props.extractCellIds(_ as ISelectableCellData).resourceId)
                        .filter(distinct);

                    return <CellMenu cell={undefined} quantization={quantization}
                                setPlan={(_plan, _viewType, _cells) => this._setPlan(_cells ?? cells, _plan, _viewType)} 
                                setActual={!this.props.actualTimeDisabled 
                                    ? (_plan, _viewType, _cells) => this._setActual(_cells ?? cells, _plan, _viewType) 
                                    : undefined}
                                viewAssignments={!this.props.actualTimeDisabled && cellsFromSingleResourceSelected.length === 1
                                    ? { action: () => this._viewAssignments(cells), disabled: !this._canResourceView(cellsFromSingleResourceSelected[0]) }
                                    : undefined} />;
                }}
                timeTypeLength={this.props.timeType.length}
            />
            {this.state.isShiftPlanPanelOpen && <ShiftAllocationPanel
                entityType={this.props.entityType}
                byResource={this.props.byResource}
                rows={this._getShiftingRows()}
                onComplete={this._shiftPlan}
                onDismiss={() => this.setState({ isShiftPlanPanelOpen: false })}
            />}
            {this.state.isAllocatePanelOpen && <AllocatePanel
                isActual={false}
                entityType={this.props.entityType}
                byResource={this.props.byResource}
                rows={this._getSelectedEditableRows()}
                onComplete={this._allocate}
                onDismiss={() => this.setState({ isAllocatePanelOpen: false })}
            />}
            {this.state.isSetActualPanelOpen && <AllocatePanel
                isActual={true}
                entityType={this.props.entityType}
                byResource={this.props.byResource}
                rows={this._getSelectedEditableRows()}
                onComplete={this._allocate}
                onDismiss={() => this.setState({ isSetActualPanelOpen: false })}
            />}
            
            {this._renderCommitDialog()}
            {this._renderChargeRateInfoDialog()}
            {this._renderCostRateTableConfirmationDialog()}
            {this._renderUnableToChangeCostRateTableDialog()}
        </>;
    }

    private _canResourceView = (resourceId: string): boolean => {
        const resource = this.props.resources.byId[resourceId];
        return resource?.isEditable || resource?.canView;
    }

    private _isCommitted = (entity: IExtensibleEntity): boolean => {
        const rowIds = this.props.extractRowIds(entity);
        return !!ResourcesListStore.firstUsage(this.props.resources.byId[rowIds.resourceIds[0]]?.usage, rowIds.entityIds[0])?.committed;
    }

    private _renderCommitDialog = () => {
        if (!this.state.isCommitting) {
            return null;
        }

        const { resources, entityType } = this.props;        
        const commits = this._getSelectedEditable().map<ResourcesListStore.ResourceUsageCommit>(_ => {
            const { entityIds, resourceIds } = this.props.extractRowIds(_);
            return { entityId: entityIds[0], resourceId: resourceIds[0] };
        });

        const resourceIds = commits.map(_ => _.resourceId).filter(distinct);
        const ids = commits.map(_ => _.entityId).filter(distinct);

        const isCommited = this.state.isCommitting === CommittingType.Uncommit;
        const title = `${isCommited ? 'Uncommit' : 'Commit'} Resource${resourceIds.length > 1 ? 's' : ''}`;
        const subText = isCommited
            ? (`Are you sure you want to uncommit` +
                ` ${resourceIds.length === 1 ? `resource "${resources.byId[commits[0].resourceId].attributes.Name}"` : `${resourceIds.length} resources`}` +
                ` from ${ids.length === 1 ? `the ${entityTypeLabelMap[entityType].singular}` : `${ids.length} entities`} and change ${resourceIds.length === 1 ? 'its' : 'their'} state to proposed?`)
            : (`Are you sure you want to commit` +
                ` ${resourceIds.length === 1 ? ` resource "${resources.byId[commits[0].resourceId].attributes.Name}"` : `${resourceIds.length} resources`}` +
                ` to ${ids.length === 1 ? `the ${entityTypeLabelMap[entityType].singular}` : `${ids.length} ${entityTypeLabelMap[entityType].plural}`}?`);

        return <RemoveDialog dialogContentProps={{ title, subText }} confirmButtonProps={{ text: isCommited ? 'Uncommit' : 'Commit' }}
            onClose={() => this.setState({ isCommitting: undefined })} onComplete={() => this._onCommit(!isCommited)} />;
    }

    private _onCommit = (commit: boolean) => {
        const commits = this._getSelectedEditable().map<ResourcesListStore.ResourceUsageCommit>(_ => {
            const { entityIds, resourceIds } = this.props.extractRowIds(_);
            return { entityId: entityIds[0], resourceId: resourceIds[0] };
        });

        if (commits) {
            this.props.resourceActions.commitUsage(this.props.entityType, commits, commit, convertToDateOnlyTimeframe(this.state.timeframe));
        }
    }

    private _setRate = (rate: number, rows?: IExtensibleEntity[]) => {
        const pairs = this._getPairsForRows(rows);
        if (!pairs.length) {
            return;
        }

        const rates = pairs.map<ResourcesListStore.ResourceUsageRate>(_ => ({ ..._, rate }));
        this.props.resourceActions.setRate(this.props.entityType, rates);
        this.setState({ isChargeRateInfoDialogOpen: true });
    }

    private _setBillingCode = (billingCode: string, rows?: IExtensibleEntity[]) => {
        const pairs = this._getPairsForRows(rows);
        if (!pairs.length) {
            return;
        }

        const billingCodes = pairs.map<ResourcesListStore.ResourceUsageBillingCode>(_ => ({ ..._, billingCode }));
        this.props.resourceActions.setBillingCode(this.props.entityType, billingCodes);
    }

    private _setStateInEntity = (stateInEntity: ResourcesListStore.ResourceEntityState, rows?: IExtensibleEntity[]) => {
        const pairs = this._getPairsForRows(rows);
        if (!pairs.length) {
            return;
        }

        const states = pairs.map<ResourcesListStore.ResourceUsageStateInEntity>(_ => ({ ..._, stateInEntity }));
        this.props.resourceActions.setStateInEntity(this.props.entityType, states);
    }

    private _getStateInEntityForRows = (rows?: IExtensibleEntity[]) => {
        const states = this._getPairsForRows(rows)
            .map(_ => calculateStateInEntity([this.props.resources.byId[_.resourceId]], [_.entityId]));

        return states.length > 0 && states.every(_ => _ === states[0]) ? states[0] : undefined;
    }

    private _requestSetCostRateTable = (costRateTableId: string, rows?: IExtensibleEntity[]) => {
        const pairs = this._getPairsForRows(rows);
        if (!pairs.length) {
            return;
        }

        const costRateTable = this.props.costRateTables.byId[costRateTableId];
        const costRateTableName = costRateTable?.name || costRateTableId;

        const targetResourceIds = pairs.map(_ => _.resourceId).filter(distinct);
        const notEligibleResourceNames = targetResourceIds
            .filter(resourceId => !this._hasCostsAndRatesForCostRateTable(resourceId, costRateTableId))
            .map(resourceId => this.props.resources.byId[resourceId]?.name ?? resourceId);

        if (notEligibleResourceNames.length) {
            this.setState({
                unableToChangeCostRateTable: {
                    costRateTableName,
                    resourceNames: notEligibleResourceNames
                }
            });
            return;
        }

        this.setState({
            pendingCostRateTableChange: {
                costRateTableId,
                costRateTableName,
                pairs
            }
        });
    }

    private _applyPendingCostRateTableChange = () => {
        const pending = this.state.pendingCostRateTableChange;
        if (!pending) {
            return;
        }

        const payload = pending.pairs.map<ResourcesListStore.ResourceUsageCostRateTable>(_ => ({
            entityId: _.entityId,
            resourceId: _.resourceId,
            costRateTableId: pending.costRateTableId
        }));
        this.props.resourceActions.setCostRateTable(this.props.entityType, payload);
        this.setState({ pendingCostRateTableChange: undefined });
    }

    private _renderChargeRateInfoDialog = () => {
        if (!this.state.isChargeRateInfoDialogOpen) {
            return null;
        }

        return <ConfirmationDialog
            onDismiss={() => this.setState({ isChargeRateInfoDialogOpen: false })}
            onYes={() => { }}
            yesButtonProps={{ text: 'Got it' }}
            dialogContentProps={{
                title: 'Consider Using Cost Rate Tables',
            }}>
            <div>
                For more flexible and accurate charge calculations, we recommend using Cost Rate Tables instead of setting Charge Rate in the resource plan.
            </div>
            <div>
                The Set Charge Rate action in the resource plans will be discontinued by the end of 2026.
            </div>
            <div>
                Please refer to this <Link target="_blank" href="https://help.ppm.express/111183-resource-management/effective-costs">article</Link> for more details.
            </div>
        </ConfirmationDialog>;
    }

    private _renderCostRateTableConfirmationDialog = () => {
        const pending = this.state.pendingCostRateTableChange;
        if (!pending) {
            return null;
        }

        const resourceIds = pending.pairs.map(_ => _.resourceId).filter(distinct);
        const isBulk = resourceIds.length > 1;
        const subText = isBulk
            ? 'This action affects the Estimated Cost, Actual Cost, and Estimated Charge calculations for selected resources in the current resource plan.\nAre you sure you want to proceed with this change?'
            : 'This action affects the Estimated Cost, Actual Cost, and Estimated Charge calculations for this resource in the current resource plan.\nAre you sure you want to proceed with this change?';

        return <ConfirmationDialog
            onDismiss={() => this.setState({ pendingCostRateTableChange: undefined })}
            onYes={this._applyPendingCostRateTableChange}
            onNo={() => this.setState({ pendingCostRateTableChange: undefined })}
            yesButtonProps={{ text: 'Proceed' }}
            noButtonProps={{ text: 'Cancel' }}
            dialogContentProps={{
                title: 'Change Cost Rate Table',
                subText
            }} />;
    }

    private _renderUnableToChangeCostRateTableDialog = () => {
        const state = this.state.unableToChangeCostRateTable;
        if (!state) {
            return null;
        }

        const isBulk = state.resourceNames.length > 1;
        const subText = isBulk
            ? `The following resources do not have costs or rates defined for the "${state.costRateTableName}" cost rate table:\n
                ${state.resourceNames.join('\n')}
                \nSelect a different cost rate table for these resources or define the resources’ costs and rates for the ${state.costRateTableName} cost rate table.`
            : `The following resource does not have costs or rates defined for the "${state.costRateTableName}" cost rate table:\n
                ${state.resourceNames[0]}
                \nSelect a different cost rate table for this resource or define the resource’s costs and rates for the ${state.costRateTableName} cost rate table.`;

        return <ConfirmationDialog
            onDismiss={() => this.setState({ unableToChangeCostRateTable: undefined })}
            onYes={() => { }}
            yesButtonProps={{ text: 'Got it' }}
            dialogContentProps={{
                title: 'Unable to Change Cost Rate Table',
                subText
            }} />;
    }

    private _getPairsForRows = (rows?: IExtensibleEntity[]) => {
        const selectedRows = rows ?? this._getSelectedEditable();
        const pairs = selectedRows
            .map(row => {
                const { entityIds, resourceIds, isTotalRow } = this.props.extractRowIds(row);
                if (isTotalRow) {
                    return undefined;
                }
                if (entityIds.length !== 1 || resourceIds.length !== 1) {
                    return undefined;
                }
                return { entityId: entityIds[0], resourceId: resourceIds[0] };
            })
            .filter(notUndefined);

        return pairs.filter((value, index, self) =>
            self.findIndex(_ => _.entityId === value.entityId && _.resourceId === value.resourceId) === index);
    }

    private _getAvailableCostRateTablesForRow = (row: IExtensibleEntity) => {
        const { resource, isTotalRow } = this._extractRowInfo(row);
        if (isTotalRow || !resource) {
            return [];
        }
        return this.props.costRateTables.allIds
            .map(id => this.props.costRateTables.byId[id])
            .filter(notUndefined)
            .filter(table => this._hasCostsAndRatesForCostRateTable(resource.id, table.id));
    }

    private _hasCostsAndRatesForCostRateTable = (resourceId: string, costRateTableId: string) => {
        const resource = this.props.resources.byId[resourceId];
        if (!resource) {
            return false;
        }

        const hasRates = !!resource.timephasedRates?.some(_ =>
            _.attributes?.CostRateTable?.id === costRateTableId
            && (_.attributes?.StandardRate ?? null) !== null);

        const hasCosts = !!resource.timephasedCosts?.some(_ =>
            _.attributes?.CostRateTable?.id === costRateTableId
            && (_.attributes?.StandardCost ?? null) !== null);

        return hasRates && hasCosts;
    }

    private _setPlan = (cells: ISelectableCellData[], value: number | true, viewType?: ViewType) => {
        const usages = this._getUsages(cells, value, viewType);
        this._debouncedSetPlan.callAction(usages);
    }

    private _setActual = (cells: ISelectableCellData[], value: number | true, viewType?: ViewType) => {
        const usages = this._getUsages(cells, value, viewType);
        this._debouncedSetActual.callAction(usages);
    }

    private _viewAssignments = (selectedCells: ISelectableCellData[]) => {
        const cellsIds = selectedCells.map(_ => this.props.extractCellIds(_));
        const resourceIds = cellsIds.map(_ => _.resourceId).filter(distinct);

        const entityIds = cellsIds.map(_ => _.entityId).filter(distinct);
        const entities = entityIds.map(_ => ({ id: _, name: this.props.entitiesMap[_].attributes.Name }));
        const entitiesWithExtraParents = entityIds.reduce((prev, _) => [...prev, ...(this.props.parentToChildrenMap[_] ?? [])], entities)

        const { minDate, maxDate } = minMax([...selectedCells.map(_ => _.start), ...selectedCells.map(_ => _.finish)]);
        const filterState: LocationState =
        {
            filters: {
                [EntityType.MyWork]: {
                    prefilterKey: undefined,
                    filter: new ActiveFilter(entities.length === 1 ? entities[0].name : "Custom")
                        .withParents(entitiesWithExtraParents)
                        .withValues([{
                            name: nameof<MyWorkAttrs>("StartDate"),
                            value: { to: maxDate!.toDateOnlyString() }
                        },
                        {
                            name: nameof<MyWorkAttrs>("DueDate"),
                            value: { from: minDate!.toDateOnlyString() }
                        }])
                        .build()
                }
            }
        };
        this.props.history.push({
            pathname: `/resource/${resourceIds[0]}/workload`,
            state: filterState
        });
    }

    private _getShiftingRows = (): { resource: ResourcesListStore.Resource, entity: IExtensibleEntity & IWithResourcePlan }[] => {
        return (this._getSelectedEditable())
            .map(_ => {
                const { entityIds, resourceIds } = this.props.extractRowIds(_);
                const resource = this.props.resources.byId[resourceIds[0]];
                const entity = this.props.entitiesMap[entityIds[0]];
                if (!resource || !entity || !ResourcesListStore.findUsage(resource.usage, entity.id, usage => !!usage.plannedHours)) {
                    return undefined;
                }

                return { resource, entity };
            })
            .filter(notUndefined);
    };

    private _getSelectedEditableRows = (): { resource: ResourcesListStore.Resource, entity: IExtensibleEntity & IWithResourcePlan }[] => {
        return (this._getSelectedEditable())
            .map(_ => {
                const { entityIds, resourceIds } = this.props.extractRowIds(_);
                const resource = this.props.resources.byId[resourceIds[0]];
                const entity = this.props.entitiesMap[entityIds[0]];
                if (!resource || !entity) {
                    return undefined;
                }

                return { resource, entity };
            })
            .filter(notUndefined);
    };

    private _shiftPlan = (startDate: Date, daysShift: number, useCalendar: boolean) => {
        const usage = this._getShiftingRows()
            .map<ResourcesListStore.ResourceUsageUpdate | undefined>(row => {
                const usages = ResourcesListStore.filterUsage(row.resource.usage, row.entity.id, _ => _.plannedHours !== 0 && _.date.getTime() >= startDate.getTime());
                const maxPlannedDate = max(usages.map(_ => _.date));
                if (!maxPlannedDate) {
                    return undefined;
                }
                return {
                    resourceId: row.resource.id,
                    entityId: row.entity.id,
                    startDate,
                    finishDate: maxPlannedDate,
                    daysShift,
                    useCalendar
                };
            }).filter(notUndefined);
        this.props.resourceActions.updateUsage(this.props.entityType, usage, convertToDateOnlyTimeframe(this.state.timeframe));
    }

    private _allocate = (start: Date, finish: Date, value: number | true, viewType?: ViewType, isActual?: boolean, valuePerDay?: boolean) => {
        const auto = value === true ? value : undefined;
        const valueNumber = value === true ? undefined : value;
        const usage = this._getSelectedEditableRows()
            .map<ResourcesListStore.ResourceUsageUpdate | undefined>(_ => ({
                resourceId: _.resource.id,
                entityId: _.entity.id,
                startDate: start,
                finishDate: finish.getBeginOfDay(),
                auto,
                valuePerDay,
                fte: viewType === ViewType.FTE ? valueNumber : undefined,
                percent: viewType === ViewType.Percent && valueNumber !== undefined
                    ? valueNumber / HUNDRED_PCT
                    * (TimephasedValueService.tryGetTimephasedValueFromResource('MaxUnitsPct', _.resource, start) ?? 0) / HUNDRED_PCT
                    * (HUNDRED_PCT - (TimephasedValueService.tryGetTimephasedValueFromResource('NonProjectWorkPct', _.resource, start) ?? 0)) / HUNDRED_PCT
                    : undefined,
                hours: viewType === ViewType.Hours ? valueNumber : undefined
            })).filter(notUndefined);
        this.props.resourceActions.updateUsage(this.props.entityType, usage, convertToDateOnlyTimeframe(this.state.timeframe), isActual);
    }

    private _getUsages = (cells: ISelectableCellData[], value: number | true, timeType?: ViewType): ResourcesListStore.ResourceUsageUpdate[] => {
        const auto = value === true ? value : undefined;
        const hoursNumber = value === true ? undefined : value;
        return cells.map<ResourcesListStore.ResourceUsageUpdate>(_ => {
            const { resourceId, entityId } = this.props.extractCellIds(_);
            const resource = this.props.resources.byId[resourceId];
            return {
                resourceId: resourceId,
                entityId: entityId,
                startDate: _.start,
                finishDate: _.finish.getBeginOfDay(),
                auto,
                fte: timeType === ViewType.FTE ? hoursNumber : undefined,
                percent: timeType === ViewType.Percent && hoursNumber !== undefined
                    ? hoursNumber / HUNDRED_PCT
                    * (TimephasedValueService.tryGetTimephasedValueFromResource('MaxUnitsPct', resource, _.start) ?? 0) / HUNDRED_PCT
                    * (HUNDRED_PCT - (TimephasedValueService.tryGetTimephasedValueFromResource('NonProjectWorkPct', resource, _.start) ?? 0)) / HUNDRED_PCT
                    : undefined,
                hours: timeType === ViewType.Hours ? hoursNumber : undefined
            };
        });
    }

    private _buildListProps = (): IListProps & Partial<ITimelineProps> => {
        return {
            ...this.props.listProps,
            scaleMultiplier: this.props.timeType.length + SCALE_MULTIPLIER_PER_TIMETYPE_COLUMN,
            fields: [
                ...this.props.listProps.fields,
                ...this.props.calculatedFields
            ],
            isFieldFake: (field: Field) => this.props.listProps.isFieldFake?.(field) || false,
            onItemRender: (entity: IExtensibleEntity, index: number, field: Field, defaultRender) => {
                if (field.id === bookingTypeField.id) {
                    const rowInfo = this._extractRowInfo(entity);
                    return rowInfo.isTotalRow ? <></> : <ResourceBookingType resource={rowInfo.resource} entityId={rowInfo.entityId} />;
                }

                if (field.id === stateInEntityFieldId) {
                    const rowInfo = this._extractRowInfo(entity);
                    return rowInfo.isTotalRow ? <></> : <ResourceStateInEntity resource={rowInfo.resource} entityId={rowInfo.entityId} />;
                }

                if (field.id === billingCodeField.id) {
                    const rowInfo = this._extractRowInfo(entity);
                    return rowInfo.isTotalRow ? <></> : <ResourceBillingCode resource={rowInfo.resource} entityId={rowInfo.entityId} />;
                }

                if (field.id === costRateTableField.id) {
                    const rowInfo = this._extractRowInfo(entity);
                    return rowInfo.isTotalRow ? <></> : <ResourceCostRateTable resource={rowInfo.resource} entityId={rowInfo.entityId} />;
                }

                const { user, extractRowIds, resources, listProps: { onItemRender } } = this.props;
                const { entityIds, resourceIds, isTotalRow } = extractRowIds(entity);
                const calculatedFieldProps: BaseCalculatedFieldFormatterProps = {
                    user,
                    resources,
                    entityIds,
                    resourceIds,
                    className: isTotalRow ? 'usage-total' : undefined
                };

                if (!isTotalRow && field.id === chargeRateField.id) {
                    const { resource, entityId } = this._extractRowInfo(entity);
                    const mode = resource?.usage?.byEntity?.[entityId]?.attributes.estimatedChargeCalculationMode
                        ?? EstimatedChargeCalculationMode.UseChargeRate;
                    if (mode === EstimatedChargeCalculationMode.UseCostRateTables) {
                        return <span>N/A</span>;
                    }

                    return <CalculatedFieldFormatter calculatedField={chargeRateField} formatType={FormatType.Cost} {...calculatedFieldProps} />
                }
                if (field.id === estimatedCostField.id) {
                    return <CalculatedFieldFormatter calculatedField={estimatedCostField} formatType={FormatType.Cost} {...calculatedFieldProps} />
                }
                if (field.id === estimatedChargeField.id) {
                    return <CalculatedFieldFormatter calculatedField={estimatedChargeField} formatType={FormatType.Cost} {...calculatedFieldProps} />
                }
                if (field.id === totalPlannedField.id) {
                    return <CalculatedFieldFormatter calculatedField={totalPlannedField} formatType={FormatType.Duration} {...calculatedFieldProps} />
                }
                if (field.id === totalActualField.id) {
                    return <CalculatedFieldFormatter calculatedField={totalActualField} formatType={FormatType.Duration} {...calculatedFieldProps} />
                }
                if (field.id === actualCostField.id) {
                    return <CalculatedFieldFormatter calculatedField={actualCostField} formatType={FormatType.Cost} {...calculatedFieldProps} />
                }

                return onItemRender ? onItemRender(entity, index, field, defaultRender) : defaultRender();
            },
            extractEntityForSorting: (row: IRow, orderBy: IOrderBy | IOrderBy[] | undefined) => {
                const arr = Array.isArray(orderBy) ? orderBy : orderBy ? [orderBy] : [];
                const { extractRowIds, resources, calculatedFields } = this.props;
                let result = row.entity;
                calculatedFields.forEach(extendWithCalculatedField);

                return result;

                function extendWithCalculatedField(field: CalculatedField) {
                    if (!arr.find(_ => _.fieldName === field.name)) {
                        return;
                    }
                    const rowIds = extractRowIds(row.entity);
                    result = {
                        ...result,
                        attributes: {
                            ...result.attributes,
                            [field.name]: field.calculate(rowIds.resourceIds.map(_ => resources.byId[_]).filter(notUndefined), rowIds.entityIds)
                        }
                    };
                }
            }
        };
    }

    private _buildMenuProps = () : ListMenuProps => {
        const { menuProps } = this.props;
        const selectedEditable = this._getSelectedEditable();
        let newMenuProps: ListMenuProps = {
            ...menuProps,
            commands: menuProps.commands,
            selectionMode: menuProps.selectionMode
                ? {
                    ...menuProps.selectionMode,
                    items: selectedEditable.length > 0
                        ? [
                            this._buildAllocateCommand(),
                            this._buildSetActualCommand(),
                            this._buildShiftAllocationCommand(),
                            buildSetStateInEntityCommand(state => this._setStateInEntity(state, selectedEditable), this.props.entityType,
                                this._getStateInEntityForRows(selectedEditable)),
                            buildSetCostRateTableCommand(id => this._requestSetCostRateTable(id, selectedEditable), this.props.costRateTables.allIds.map(id => this.props.costRateTables.byId[id])),
                            this._buildSetRateCommandIfCanManageBudget(selectedEditable),
                            buildSetBillingCodeCommand(code => this._setBillingCode(code, selectedEditable)),
                            this._buildCommitCommand(selectedEditable),
                            this._buildUncommitCommand(selectedEditable),
                            ...menuProps.selectionMode.items,
                        ].filter(notUndefined)
                        : menuProps.selectionMode.items
                }
                : undefined
        };

        if ('fields' in this.props.menuProps) {
            newMenuProps = {
                ...newMenuProps,
                fields: [
                    ...(newMenuProps as { fields: Field[]; }).fields,
                    ...this.props.calculatedFields
                ],
            } as ListMenuProps;
        }
        if(!newMenuProps.farCommands){
            newMenuProps.farCommands = [];
        }
        newMenuProps.farCommands.push({
            key: 'timeType',
            onRender: () => {
                const allowedTypes = this.props.actualTimeDisabled ? ['plan', 'capacity'] : allTimeTypes;
                return <TimeTypeSelector<TimeType> allTimeTypes={Object.values(this.props.timeTypesConfig).filter(_ => allowedTypes.includes(_.type))}
                    timeType={this.props.timeType}
                    onTimeTypeChange={(timeType, checked) => this.props.onTimeTypeChange(allTimeTypes.filter(_ => _ === timeType ? checked : this.props.timeType.includes(_)))
                } />;
            } 
        })
        return newMenuProps;
    }

    private _getSelectedEditable() {
        const { isEditable } = this.props;
        const selected = this.props.listProps.selection!.getSelection() as IExtensibleEntity[];
        const selectedEditable = selected.filter(_ => isEditable?.(_));
        return selectedEditable;
    }

    private _buildShiftAllocationCommand() {
        return !!this._getShiftingRows().length ? {
            key: 'shift',
            text: 'Shift Allocation',
            iconProps: { iconName: 'Switch' },
            onClick: () => this.setState({ isShiftPlanPanelOpen: true }),
        } : undefined;
    }

    private _buildSetActualCommand() {
        return !this.props.actualTimeDisabled ? {
            key: 'setActual',
            text: 'Set Actual',
            iconProps: { iconName: 'UserGauge' },
            onClick: () => this.setState({ isSetActualPanelOpen: true })
        } : undefined;
    }

    private _buildAllocateCommand() {
        return {
            key: 'allocate',
            text: 'Allocate',
            iconProps: { iconName: 'UserGauge' },
            onClick: () => this.setState({ isAllocatePanelOpen: true })
        };
    }

    private _buildCommitUncommitCommand(item: IExtensibleEntity) {
        const isCommitted = this._isCommitted(item);
        return !this.props.commitDisabled ? {
            key: 'commit',
            name: isCommitted ? 'Uncommit' : 'Commit',
            iconProps: { iconName: 'Commitments' },
            onClick: () => this.setState({ isCommitting: isCommitted ? CommittingType.Uncommit : CommittingType.Commit })
        } : undefined;
    }

    private _buildCommitCommand(selectedEntities: IExtensibleEntity[]) {
        return (!this.props.commitDisabled && selectedEntities.some(_ => !this._isCommitted(_))) ? {
            key: 'commit',
            name: 'Commit',
            iconProps: { iconName: 'Commitments' },
            onClick: () => this.setState({ isCommitting: CommittingType.Commit })
        } : undefined;
    }

    private _buildUncommitCommand(selectedEntities: IExtensibleEntity[]) {
        return (!this.props.commitDisabled && selectedEntities.some(_ => this._isCommitted(_))) ? {
            key: 'uncommit',
            name: 'Uncommit',
            iconProps: { iconName: 'Commitments' },
            onClick: () => this.setState({ isCommitting: CommittingType.Uncommit })
        } : undefined;
    }

    private _buildSetRateCommandIfCanManageBudget(rows: IExtensibleEntity[]): IContextualMenuItem | undefined {
        const canManageBudget = contains(this.props.user.permissions.common, CommonOperations.BudgetManage);
        if (!canManageBudget) {
            return undefined;
        }

        const canSetRateForAllRows = rows.every(row => {
            const { resource, entityId, isTotalRow } = this._extractRowInfo(row);
            if (isTotalRow || !resource) {
                return false;
            }

            const mode = resource.usage?.byEntity?.[entityId]?.attributes.estimatedChargeCalculationMode
                ?? EstimatedChargeCalculationMode.UseChargeRate;
            return mode === EstimatedChargeCalculationMode.UseChargeRate;
        });

        return canSetRateForAllRows ? buildSetRateCommand(rate => this._setRate(rate, rows)) : undefined;
    }

    private _extractRowInfo = (entity: IExtensibleEntity) => {
        const { entityIds, resourceIds, isTotalRow } = this.props.extractRowIds(entity);
        const resource = this.props.resources.byId[resourceIds[0]];
        const entityId = entityIds[0];
        return { resource, entityId, isTotalRow };
    }
}

function extractParentToChildrenMap(ownProps: OwnProps, state: ApplicationState) {
    const parentToChildrenMap: Dictionary<MyWorkParentInfo[]> = {};
    if (ownProps.entityType === EntityType.Portfolio) {
        state.portfolios.allIds.forEach(portfolioId => {
            const projectsSet = new Set(state.portfolios.byId[portfolioId]?.projectIds);
            const programsSet = new Set(state.portfolios.byId[portfolioId]?.programIds);
            state.portfolios.byId[portfolioId]?.programIds.forEach(programId => {
                state.programs.byId[programId]?.projectIds.forEach(projectId => projectsSet.add(projectId));
            });

            parentToChildrenMap[portfolioId] = [
                ...Array.from(programsSet).map(_ => ({ id: _, name: state.programs.byId[_]?.attributes.Name })),
                ...Array.from(projectsSet).map(_ => ({ id: _, name: state.projectsList.byId[_]?.attributes.Name }))
            ];
        });
    }
    if (ownProps.entityType === EntityType.Program) {
        state.programs.allIds.forEach(programId => {
            parentToChildrenMap[programId] = state.programs.byId[programId].projectIds.map(projectId =>
                ({ id: projectId, name: state.projectsList.byId[projectId]?.attributes.Name })) ?? [];
        });
    }
    return parentToChildrenMap;
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StoreProps {
    const parentToChildrenMap = extractParentToChildrenMap(ownProps, state);

    return {
        user: state.user,
        resources: state.resources,
        parentToChildrenMap,
        firstDayOfWeek: getFirstDayOfWeekSetting(state),
        costRateTables: state.costRateTables
    };
}

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        resourceActions: bindActionCreators(ResourcesListStore.actionCreators, dispatch),
    }
}

export default withRouter<OwnProps>(connect(mapStateToProps, mergeActionCreators)(ResourceUsageGrid));

type ViewAssignments =
    {
        action: (cells?: ISelectableCellData[]) => void;
        disabled: boolean;
    }

const CellMenu = (props: {
    cell: ISelectableCellData | undefined, quantization: Quantization, setPlan: SetTimeAction, setActual?: SetTimeAction,
    viewAssignments?: ViewAssignments
}): JSX.Element => {
    const { cell, quantization, setPlan, setActual, viewAssignments } = props;
    const [mode, setMode] = React.useState<undefined | 'plan' | 'actual'>(undefined);

    const action = mode === 'plan' ? setPlan : mode === 'actual' ? setActual : undefined;
    const text = mode === 'plan' ? 'Allocated' : 'Actual';

    const buildSetTimeBtn = (timeLabel: string, btnAction: () => void): ICommandBarItemProps => ({
        key: timeLabel,
        onRender: () => <CommandBarButton iconProps={{ iconName: 'UserGauge' }} text={timeLabel} onClick={btnAction}
            onRenderText={(_, defaultRender) => <div style={{ display: 'flex' }}>{defaultRender?.(_)}<Icon iconName="ChevronRight" />
            </div>} />
    });

    return <CommandBar className="header-command-bar" items={mode === undefined
        ? [
            buildSetTimeBtn('Allocate', () => setMode('plan')),
            setActual ? buildSetTimeBtn('Set Actual', () => setMode('actual')) : undefined,
            viewAssignments ? {
                key: 'viewAssigments',
                onRender: () => <CommandBarButton
                    iconProps={{ iconName: 'PPMXSectionActionItems' }}
                    text="View Assignments"
                    disabled={viewAssignments.disabled}
                    onClick={() => viewAssignments.action(cell && [cell])} />
            } : undefined
        ].filter(notUndefined)
        : [
            { key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => setMode(undefined) },
            buildAutoAllocateCommand(cell && [cell], action!, text),
            buildAllocateCommands(ViewType.Hours, cell && [cell], quantization, action!, text)!,
            buildAllocateCommands(ViewType.Percent, cell && [cell], quantization, action!, text)!,
            buildAllocateCommands(ViewType.FTE, cell && [cell], quantization, action!, text)!
        ]} styles={{ root: { padding: "unset", minWidth: 440 } }} />;
}

type ResourceUsageCellProps = {
    resource: ResourcesListStore.Resource;
    entityId: string;
};

export const ResourceBookingType = (props: ResourceUsageCellProps) => {
    const committed = bookingTypeField.calculate([props.resource], [props.entityId]);
    return committed !== undefined
        ? <div className="resource-type">
            <div className={`align-center status ${committed ? "Green" : "Gray"}`}>
                {committed ? "Committed" : "Proposed"}
            </div>
        </div>
        : null;
}

const ResourceStateInEntity = (props: ResourceUsageCellProps) => {
    const stateInEntity = calculateStateInEntity([props.resource], [props.entityId]);
    return stateInEntity === undefined
        ? null
        : <StateInEntityView state={stateInEntity} />;
}

const ResourceBillingCode = (props: ResourceUsageCellProps) => {
    const billingCode = billingCodeField.calculate([props.resource], [props.entityId]);
    return billingCode !== undefined ? <TextFormatter value={billingCode} /> : null;
}

const ResourceCostRateTable = connect(
    (state: ApplicationState) => ({ costRateTables: state.costRateTables })
)((props: ResourceUsageCellProps & { costRateTables: CostRateTableStore.CostRateTableState }) => {
    const costRateTableId = costRateTableField.calculate([props.resource], [props.entityId]);
    if (!costRateTableId) {
        return null;
    }
    
    const costRateTable = props.costRateTables.byId[costRateTableId];
    const displayValue = costRateTable?.name || costRateTableId;
    
    return <TextFormatter value={displayValue} />;
});

export const onScaleChanged = (resourceIds: string[], callback: (resourceIds: string[], timeframe: IScalableTimeframe) => void,
    timelineChange: TimelineChange): void => {
    const { prevState, newState, origin, prevOrigin } = timelineChange;

    const newTimeline = {
        start: origin?.timeframe?.start || newState.scale.dates[0].start,
        end: origin?.timeframe?.end || newState.scale.dates[newState.scale.dates.length - 1].finish
    };

    const prevTimeline = prevState
        ? {
            start: prevOrigin?.timeframe?.start || prevState.scale.dates[0].start,
            end: prevOrigin?.timeframe?.end || prevState.scale.dates[prevState.scale.dates.length - 1].finish
        }
        : undefined;

    if (!prevTimeline
        || newTimeline.start.getTime() !== prevTimeline.start.getTime()
        || newTimeline.end.getTime() !== prevTimeline.end.getTime()) {
            callback(resourceIds, convertToDateOnlyTimeframe(newTimeline));
        }
}

export const convertToDateOnlyTimeframe = (timeframe: ITimeframe): IScalableTimeframe => ({
    startDate: timeframe.start.toDateOnlyString(),
    endDate: timeframe.end.toDateOnlyString()
})